import React from "react";
import { Modal, Button, Spinner, Alert, Form } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import SettingsForm from "./SettingsForm";
import CompanyStep from "./CompanyStep";
import { SETTINGS_KEYS } from "./HierarchyManagementConstants";
import type { WizardData } from "./HierarchyManagement";
import type { Company } from "../types/auth";

interface WizardModalProps {
  show: boolean;
  onHide: () => void;
  modalType: "group" | "location" | "company" | null;
  modalAction: "create" | "edit" | "assign-company";
  wizardStep: number;
  wizardData: WizardData;
  setWizardData: React.Dispatch<React.SetStateAction<WizardData>>;
  companyMode: "attach" | "create";
  setCompanyMode: React.Dispatch<React.SetStateAction<"attach" | "create">>;
  override: Record<string, boolean>;
  setOverride: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
  companyOverride: Record<string, boolean>;
  setCompanyOverride: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
  modalError: string | null;
  modalSuccess: string | null;
  actionLoading: boolean;
  onNext: () => void;
  onBack: () => void;
  onSubmit: () => void;
  availableCompanies: Company[];
}

const WizardModal: React.FC<WizardModalProps> = ({
  show,
  onHide,
  modalType,
  modalAction,
  wizardStep,
  wizardData,
  setWizardData,
  companyMode,
  setCompanyMode,
  override,
  setOverride,
  companyOverride,
  setCompanyOverride,
  modalError,
  modalSuccess,
  actionLoading,
  onNext,
  onBack,
  onSubmit,
  availableCompanies,
}) => {
  const { t } = useTranslation('pages', { keyPrefix: 'wizardModal' });
  const wizardTotalSteps =
    modalAction === "assign-company" || modalType === "company"
      ? 1
      : modalType === "group"
        ? modalAction === "edit"
          ? 1
          : 3
        : modalAction === "edit"
          ? 1
          : 2;

  const renderWizardStep = () => {
    if (modalAction === "assign-company" || modalType === "company") {
      return (
        <CompanyStep
          modalAction={modalAction}
          wizardData={wizardData}
          setWizardData={setWizardData}
          companyMode={companyMode}
          setCompanyMode={setCompanyMode}
          companyOverride={companyOverride}
          setCompanyOverride={setCompanyOverride}
          availableCompanies={availableCompanies}
        />
      );
    }
    if (modalType === "group") {
      switch (wizardStep) {
        case 1:
          return (
            <>
              <h3 className="an-wizard-step-title">{t('groupStep1')}</h3>
              <Form.Group className="mb-3">
                <Form.Label>{t('groupName')}</Form.Label>
                <Form.Control
                  type="text"
                  value={wizardData.name || ""}
                  onChange={(e) =>
                    setWizardData((d) => ({ ...d, name: e.target.value }))
                  }
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>{t('description')}</Form.Label>
                <Form.Control
                  as="textarea"
                  value={wizardData.description || ""}
                  onChange={(e) =>
                    setWizardData((d) => ({
                      ...d,
                      description: e.target.value,
                    }))
                  }
                />
              </Form.Group>
              <SettingsForm
                settings={SETTINGS_KEYS}
                wizardData={wizardData}
                setWizardData={setWizardData}
                override={{}}
                setOverride={() => {}}
                showOverride={false}
              />
            </>
          );
        case 2:
          return (
            <>
              <h3 className="an-wizard-step-title">{t('groupStep2')}</h3>
              <Form.Group className="mb-3">
                <Form.Label>{t('locationName')}</Form.Label>
                <Form.Control
                  type="text"
                  value={wizardData.location_name || ""}
                  onChange={(e) =>
                    setWizardData((d) => ({
                      ...d,
                      location_name: e.target.value,
                    }))
                  }
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>{t('locationDescription')}</Form.Label>
                <Form.Control
                  as="textarea"
                  value={wizardData.location_description || ""}
                  onChange={(e) =>
                    setWizardData((d) => ({
                      ...d,
                      location_description: e.target.value,
                    }))
                  }
                />
              </Form.Group>
              <SettingsForm
                settings={SETTINGS_KEYS}
                wizardData={wizardData}
                setWizardData={setWizardData}
                override={override}
                setOverride={setOverride}
                prefix="location"
                parentData={wizardData}
              />
            </>
          );
        case 3:
          return (
            <CompanyStep
              modalAction={modalAction}
              wizardData={wizardData}
              setWizardData={setWizardData}
              companyMode={companyMode}
              setCompanyMode={setCompanyMode}
              companyOverride={companyOverride}
              setCompanyOverride={setCompanyOverride}
              availableCompanies={availableCompanies}
            />
          );
        default:
          return null;
      }
    } else if (modalType === "location") {
      switch (wizardStep) {
        case 1:
          return (
            <>
              <h3 className="an-wizard-step-title">{t('locationStep1')}</h3>
              <Form.Group className="mb-3">
                <Form.Label>{t('locationName')}</Form.Label>
                <Form.Control
                  type="text"
                  value={wizardData.location_name || ""}
                  onChange={(e) =>
                    setWizardData((d) => ({
                      ...d,
                      location_name: e.target.value,
                    }))
                  }
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>{t('locationDescription')}</Form.Label>
                <Form.Control
                  as="textarea"
                  value={wizardData.location_description || ""}
                  onChange={(e) =>
                    setWizardData((d) => ({
                      ...d,
                      location_description: e.target.value,
                    }))
                  }
                />
              </Form.Group>
              <SettingsForm
                settings={SETTINGS_KEYS}
                wizardData={wizardData}
                setWizardData={setWizardData}
                override={override}
                setOverride={setOverride}
                prefix="location"
                parentData={wizardData}
              />
            </>
          );
        case 2:
          return (
            <CompanyStep
              modalAction={modalAction}
              wizardData={wizardData}
              setWizardData={setWizardData}
              companyMode={companyMode}
              setCompanyMode={setCompanyMode}
              companyOverride={companyOverride}
              setCompanyOverride={setCompanyOverride}
              availableCompanies={availableCompanies}
            />
          );
        default:
          return null;
      }
    }
    return null;
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" dialogClassName="an-wizard-modal" contentClassName="an-modal-panel an-wizard-panel">
      <Modal.Header closeButton className="an-section-divider">
        <Modal.Title>
          {modalAction === "create"
            ? t('titleCreate')
            : modalAction === "edit"
              ? t('titleEdit')
              : t('titleAssign')}{" "}
          {modalType === 'group' ? t('typeGroup') : modalType === 'location' ? t('typeLocation') : modalType === 'company' ? t('typeCompany') : modalType}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="an-enterprise-page an-settings-page an-wizard-body">
        {modalError && <Alert variant="danger">{modalError}</Alert>}
        {modalSuccess && <Alert variant="success">{modalSuccess}</Alert>}
        {renderWizardStep()}
      </Modal.Body>
      <Modal.Footer className="an-section-divider">
        {wizardStep > 1 && (
          <Button variant="secondary" onClick={onBack}>
            {t('back')}
          </Button>
        )}
        {wizardStep < wizardTotalSteps && (
          <Button variant="primary" onClick={onNext}>
            {t('next')}
          </Button>
        )}
        {wizardStep === wizardTotalSteps && (
          <Button
            variant="primary"
            onClick={onSubmit}
            disabled={actionLoading}
          >
            {actionLoading ? (
              <Spinner size="sm" />
            ) : modalAction === "create" ? (
              t('finish')
            ) : modalAction === "assign-company" ? (
              t('assign')
            ) : (
              t('saveChanges')
            )}
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default WizardModal;
